import * as React from 'react';

import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormHelperText,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  createFilterOptions,
} from '@mui/material';
import RangeFields from 'components/ReportRequestV2/RangeFields';

import styles from './FlavorConstraintPicker.module.css';

export type FlavorConstraintMode = 'free' | 'lock' | 'remove' | 'tune';
type ActiveMode = Exclude<FlavorConstraintMode, 'free'>;

export interface FlavorOption {
  flavor: string;
  label: string;
  mode: FlavorConstraintMode;
  /** The target product's share of the flavor, in percent. */
  sharePercent?: number;
  inReport: boolean;
}

export interface FlavorConstraintPickerProps {
  /** Every flavor that can be constrained, constrained ones included. */
  options: FlavorOption[];
  /** The product's top unconstrained flavors, offered as one-click adds. */
  suggestions: FlavorOption[];
  tuneRanges: ReadonlyMap<string, { min: number; max: number }>;
  onModeChange: (flavors: string[], mode: FlavorConstraintMode) => void;
  onTuneRangeChange: (
    flavor: string,
    range: { min: number; max: number },
  ) => void;
  tuneBounds: [number, number];
  loading?: boolean;
  /** Explains why there are no product shares or suggestions, if so. */
  notice?: string;
  /** Whether the target product's shares are known, so a missing share means 0%. */
  sharesKnown?: boolean;
  /** Search every flavor rather than only the product's decomp flavors. */
  offerAll: boolean;
  /** Absent when all flavors are always offered, e.g. without product shares. */
  onOfferAllChange?: (offerAll: boolean) => void;
  tuneErrors?: Record<string, string>;
  error?: string;
}

const MODES: { mode: ActiveMode; label: string }[] = [
  { mode: 'remove', label: 'Remove' },
  { mode: 'lock', label: 'Lock' },
  { mode: 'tune', label: 'Tune' },
];

/**
 * New constraints start as Lock. A flavor the product doesn't contain starts as
 * Tune (2–8%) instead, since locking it at 0% would just remove it.
 */
const addMode = (o: FlavorOption, sharesKnown: boolean): ActiveMode =>
  sharesKnown && o.sharePercent === undefined ? 'tune' : 'lock';

const currentText = (o: FlavorOption, sharesKnown: boolean) => {
  if (o.sharePercent !== undefined) return `${o.sharePercent}%`;
  return sharesKnown ? '0%' : 'unknown';
};

const rowHint = (o: FlavorOption, sharesKnown: boolean) => {
  const now = currentText(o, sharesKnown);
  if (o.mode === 'lock')
    return now === 'unknown' ? 'Kept at its current share' : `Kept at ${now}`;
  return 'Removed from the recipe';
};

const NOT_IN_REPORT =
  "Not in the parent report, so the optimizer can't lock or tune it.";
const OPTION_LIMIT = 100;
const filterOptions = createFilterOptions<FlavorOption>({
  limit: OPTION_LIMIT,
  stringify: (o) => `${o.label} ${o.flavor}`,
});
const MODE_DONE: Record<ActiveMode, string> = {
  remove: 'Removed',
  lock: 'Locked',
  tune: 'Tuned',
};

const share = (o: FlavorOption) =>
  o.sharePercent !== undefined && (
    <span className={styles.share}>{o.sharePercent}%</span>
  );

/**
 * Add flavors by searching (or one-click from the product's top flavors), then
 * set each constrained flavor's mode on its own row. The search stays open and
 * keeps its text after each pick, with picked flavors checked, so several can
 * be added in one pass. Scales to hundreds of flavors.
 */
const FlavorConstraintPicker: React.FC<FlavorConstraintPickerProps> = ({
  options,
  suggestions,
  tuneRanges,
  onModeChange,
  onTuneRangeChange,
  tuneBounds,
  loading,
  notice,
  sharesKnown = false,
  offerAll,
  onOfferAllChange,
  tuneErrors = {},
  error,
}) => {
  const [input, setInput] = React.useState('');
  // A–Z in both the search and the constrained rows, so a flavor is always where you expect it.
  const sorted = React.useMemo(
    () => [...options].sort((a, b) => a.label.localeCompare(b.label)),
    [options],
  );
  const constrained = sorted.filter((o) => o.mode !== 'free');
  // Decomp only narrows what's listed before typing; a search always covers every flavor,
  // so a note the product doesn't contain yet can still be found and tuned.
  const browse = offerAll
    ? sorted
    : sorted.filter((o) => o.sharePercent !== undefined || o.mode !== 'free');
  const add = (o: FlavorOption) =>
    onModeChange([o.flavor], addMode(o, sharesKnown));
  const toggle = (o: FlavorOption) =>
    o.mode === 'free' ? add(o) : onModeChange([o.flavor], 'free');

  return (
    <Box className={styles.picker}>
      <Box className={styles.addRow}>
        <Autocomplete<FlavorOption, false, false, false>
          className={styles.search}
          options={sorted}
          value={null}
          inputValue={input}
          onInputChange={(_, value, reason) =>
            reason !== 'reset' && setInput(value)
          }
          onChange={(_, o) => o && toggle(o)}
          filterOptions={(_, state) =>
            filterOptions(state.inputValue.trim() ? sorted : browse, state)
          }
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.flavor === b.flavor}
          loading={loading}
          loadingText="Loading flavors…"
          disableCloseOnSelect
          blurOnSelect={false}
          clearOnBlur={false}
          noOptionsText={input ? 'No matching flavors' : 'No flavors to add'}
          renderOption={(props, o) => (
            <li {...props} key={o.flavor} aria-selected={o.mode !== 'free'}>
              <Checkbox
                size="small"
                checked={o.mode !== 'free'}
                tabIndex={-1}
                disableRipple
                className={styles.check}
              />
              <Box className={styles.option}>
                <span>{o.label}</span>
                {share(o)}
                {o.mode !== 'free' && (
                  <Typography variant="caption" color="primary">
                    {MODE_DONE[o.mode]}
                  </Typography>
                )}
                {!o.inReport && (
                  <Typography variant="caption" color="text.secondary">
                    Not in parent report
                  </Typography>
                )}
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Add a flavor constraint"
              placeholder={`Type to search ${options.length.toLocaleString()} flavors`}
            />
          )}
        />
        {onOfferAllChange && (
          // Fixed-size segments, so switching never resizes the search or shifts the layout.
          <ToggleButtonGroup
            size="small"
            exclusive
            value={offerAll ? 'all' : 'decomp'}
            onChange={(_, next: 'all' | 'decomp' | null) =>
              next && onOfferAllChange(next === 'all')
            }
            aria-label="Flavors to list"
            className={styles.scope}
          >
            <ToggleButton value="decomp">Decomp</ToggleButton>
            <ToggleButton value="all">All flavors</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {suggestions.length > 0 && (
        <Box
          className={styles.suggestions}
          role="group"
          aria-label="Top flavors in this product"
        >
          <Typography
            variant="caption"
            color="text.secondary"
            className={styles.caption}
          >
            Top flavors in this product, click to add:
          </Typography>
          {suggestions.map((o) => (
            <Chip
              key={o.flavor}
              size="small"
              variant="outlined"
              icon={<AddIcon />}
              label={`${o.label} ${o.sharePercent}%`}
              onClick={() => add(o)}
              aria-label={`Add ${o.label}, ${o.sharePercent}% of the product`}
            />
          ))}
        </Box>
      )}
      {notice && (
        <Typography variant="body2" color="text.secondary">
          {notice}
        </Typography>
      )}

      {constrained.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          className={styles.empty}
        >
          No constraints: the optimizer may change every flavor.
        </Typography>
      ) : (
        <Box
          className={styles.rows}
          role="list"
          aria-label="Constrained flavors"
        >
          {constrained.map((o) => {
            const range = tuneRanges.get(o.flavor) ?? {
              min: tuneBounds[0],
              max: tuneBounds[1],
            };
            return (
              <Box
                key={o.flavor}
                role="listitem"
                className={styles.row}
                aria-label={o.label}
              >
                <Box className={styles.name}>
                  <Typography variant="body2" fontWeight={600}>
                    {o.label}
                  </Typography>
                  {!o.inReport && (
                    <Tooltip title={NOT_IN_REPORT}>
                      <WarningAmberIcon
                        fontSize="small"
                        color="warning"
                        titleAccess={NOT_IN_REPORT}
                      />
                    </Tooltip>
                  )}
                </Box>
                <Box
                  className={styles.current}
                  aria-label={`Current decomp ${currentText(o, sharesKnown)}`}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    component="div"
                  >
                    Current
                  </Typography>
                  <Typography variant="body1" fontWeight={600} component="div">
                    {currentText(o, sharesKnown) === 'unknown'
                      ? '—'
                      : currentText(o, sharesKnown)}
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={o.mode}
                  onChange={(_, next: ActiveMode | null) =>
                    next && onModeChange([o.flavor], next)
                  }
                  aria-label={`${o.label} constraint`}
                >
                  {MODES.map((m) => (
                    <ToggleButton key={m.mode} value={m.mode}>
                      {m.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {o.mode === 'tune' ? (
                  <Box className={styles.range}>
                    <RangeFields
                      label={o.label}
                      min={range.min}
                      max={range.max}
                      onChange={(next) => onTuneRangeChange(o.flavor, next)}
                      bounds={tuneBounds}
                      unit="%"
                      error={!!tuneErrors[o.flavor]}
                      sliderClassName={styles.slider}
                      numberClassName={styles.number}
                      marks={
                        o.sharePercent !== undefined &&
                        o.sharePercent <= tuneBounds[1]
                          ? [
                              {
                                value: o.sharePercent,
                                label: `now ${o.sharePercent}%`,
                              },
                            ]
                          : undefined
                      }
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className={styles.range}
                  >
                    {rowHint(o, sharesKnown)}
                  </Typography>
                )}
                <IconButton
                  size="small"
                  aria-label={`Clear ${o.label}`}
                  onClick={() => onModeChange([o.flavor], 'free')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                {tuneErrors[o.flavor] && (
                  <FormHelperText error className={styles.rowError}>
                    {tuneErrors[o.flavor]}
                  </FormHelperText>
                )}
              </Box>
            );
          })}
          <Box>
            <Button
              size="small"
              onClick={() =>
                onModeChange(
                  constrained.map((o) => o.flavor),
                  'free',
                )
              }
            >
              Clear all
            </Button>
          </Box>
        </Box>
      )}
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  );
};

export default FlavorConstraintPicker;
